import { collection, query, where, onSnapshot, addDoc, orderBy, doc, updateDoc, serverTimestamp, getDocs, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export const getUserConversations = (userId, callback) => {
  const q = query(
    collection(db, 'conversations'),
    where('employerId', '==', userId),
    orderBy('lastMessageTime', 'desc')
  );
  
  return onSnapshot(q, (snapshot) => {
    const conversations = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(conversations);
  });
};

export const getMessages = (conversationId, callback) => {
  const q = query(
    collection(db, 'conversations', conversationId, 'messages'),
    orderBy('timestamp', 'asc')
  );
  
  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(messages);
  });
};

export const sendMessage = async (conversationId, senderId, text) => {
  try {
    await addDoc(collection(db, 'conversations', conversationId, 'messages'), {
      senderId,
      text,
      timestamp: serverTimestamp()
    });
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

export const markConversationAsRead = async (conversationId, userId) => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    
    // Check if document exists first
    const conversationDoc = await getDoc(conversationRef);
    
    if (!conversationDoc.exists()) {
      console.warn(`Conversation ${conversationId} does not exist, skipping mark as read`);
      return;
    }
    
    // Document exists, update it
    await updateDoc(conversationRef, {
      [`lastRead_${userId}`]: serverTimestamp(),
      [`unreadCount_${userId}`]: 0
    });
  } catch (error) {
    console.error('Error marking as read:', error);
  }
};

export const getUnreadCount = async (userId) => {
  try {
    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('employerId', '==', userId)
    );
    
    const conversationsSnapshot = await getDocs(conversationsQuery);
    let totalUnread = 0;

    for (const convDoc of conversationsSnapshot.docs) {
      const conversationId = convDoc.id;
      const convData = convDoc.data();
      const lastRead = convData[`lastRead_${userId}`];

      // Get messages in this conversation
      const messagesQuery = query(
        collection(db, 'conversations', conversationId, 'messages'),
        where('senderId', '!=', userId)
      );
      
      const messagesSnapshot = await getDocs(messagesQuery);

      for (const msgDoc of messagesSnapshot.docs) {
        const msgData = msgDoc.data();
        const msgTimestamp = msgData.timestamp;

        if (msgTimestamp) {
          if (!lastRead || msgTimestamp.toMillis() > lastRead.toMillis()) {
            totalUnread++;
          }
        }
      }
    }

    return totalUnread;
  } catch (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }
};

export const subscribeToUnreadCount = (userId, callback) => {
  const conversationsQuery = query(
    collection(db, 'conversations'),
    where('employerId', '==', userId)
  );

  const countUnsubs = {};
  const perConvCounts = {};

  const unsubConvs = onSnapshot(conversationsQuery, (snapshot) => {
    const currentIds = new Set(snapshot.docs.map(d => d.id));

    // Remove listeners for conversations no longer present
    for (const id of Object.keys(countUnsubs)) {
      if (!currentIds.has(id)) {
        countUnsubs[id]();
        delete countUnsubs[id];
        delete perConvCounts[id];
      }
    }

    // Add listeners for new conversations only
    for (const convDoc of snapshot.docs) {
      const conversationId = convDoc.id;
      if (countUnsubs[conversationId]) continue; // already subscribed

      countUnsubs[conversationId] = subscribeConversationUnreadCount(
        conversationId,
        userId,
        (count) => {
          perConvCounts[conversationId] = count;
          const total = Object.values(perConvCounts).reduce((sum, c) => sum + c, 0);
          callback(total);
        }
      );
    }

    // If no conversations, callback with 0
    if (snapshot.docs.length === 0) {
      callback(0);
    }
  });

  return () => {
    unsubConvs();
    Object.values(countUnsubs).forEach(fn => fn());
  };
};

export const subscribeConversationUnreadCount = (conversationId, userId, callback) => {
  const convRef = doc(db, 'conversations', conversationId);
  const msgsRef = collection(db, 'conversations', conversationId, 'messages');

  let lastRead = null;
  let msgs = [];
  let convLoaded = false;
  let msgsLoaded = false;

  function compute() {
    if (!convLoaded || !msgsLoaded) return;
    let count = 0;
    for (const msg of msgs) {
      const data = msg.data();
      if (data.senderId === userId) continue; // skip own messages
      const ts = data.timestamp;
      // If lastRead is null, all messages are unread regardless of timestamp
      if (!lastRead) {
        count++;
        continue;
      }
      // If timestamp is null (serverTimestamp not yet resolved), treat as unread
      if (!ts) {
        count++;
        continue;
      }
      if (ts.toMillis() > lastRead.toMillis()) count++;
    }
    callback(count);
  }

  const unsubConv = onSnapshot(convRef, (snap) => {
    lastRead = snap.exists() ? snap.data()[`lastRead_${userId}`] ?? null : null;
    convLoaded = true;
    compute();
  });

  const unsubMsgs = onSnapshot(msgsRef, (snap) => {
    msgs = snap.docs;
    msgsLoaded = true;
    compute();
  });

  return () => { unsubConv(); unsubMsgs(); };
};

export const getConversationUnreadCount = async (conversationId, userId) => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationDoc = await getDoc(conversationRef);
    
    if (!conversationDoc.exists()) {
      return 0;
    }
    
    const convData = conversationDoc.data();
    const lastRead = convData[`lastRead_${userId}`];
    
    // Get messages in this conversation
    const messagesQuery = query(
      collection(db, 'conversations', conversationId, 'messages'),
      where('senderId', '!=', userId)
    );
    
    const messagesSnapshot = await getDocs(messagesQuery);
    let unreadCount = 0;

    for (const msgDoc of messagesSnapshot.docs) {
      const msgData = msgDoc.data();
      const msgTimestamp = msgData.timestamp;

      if (msgTimestamp) {
        if (!lastRead || msgTimestamp.toMillis() > lastRead.toMillis()) {
          unreadCount++;
        }
      }
    }

    return unreadCount;
  } catch (error) {
    console.error('Error getting conversation unread count:', error);
    return 0;
  }
};